import { motion } from "framer-motion";
import { INDUSTRIES, IMAGES, VIDEOS } from "@/data/content";
import VideoBackdrop from "@/components/VideoBackdrop";

/** Asymmetric bento: photo/video tiles with hover zoom + HUD bracket.
 *  Tiles keep a dark plate + white text in both themes (they sit on photography). */
const SPANS = {
  food: "md:col-span-2 md:row-span-2",
  councils: "",
  farmers: "",
  pharma: "md:col-span-2",
  water: "",
  petrochemical: "",
  power: "md:col-span-2",
};

export default function Industries() {
  return (
    <section id="industries" data-testid="industries-section" className="border-y border-line/10 bg-base2 py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-2xl">
            <p className="hud-label">[ 02 / INDUSTRIES ]</p>
            <h2 className="mt-4 font-heading text-3xl font-semibold uppercase tracking-tight text-ink md:text-4xl lg:text-5xl">
              Who we work with
            </h2>
          </div>
          <span aria-hidden="true" className="hud-label hidden lg:block">SECTORS // {String(INDUSTRIES.length).padStart(2, "0")}</span>
        </motion.div>

        <div className="mt-12 grid auto-rows-[160px] grid-cols-1 gap-4 md:grid-cols-4">
          {INDUSTRIES.map((industry, i) => (
            <motion.div
              key={industry.key}
              data-testid={`industry-tile-${industry.key}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.07, ease: "easeOut" }}
              className={`group relative overflow-hidden border border-line/10 bg-[#0A0F1A] ${SPANS[industry.key]}`}
            >
              {industry.video ? (
                <VideoBackdrop
                  sources={VIDEOS[industry.video].sources}
                  poster={IMAGES[industry.key]}
                  alt={`${industry.name} — wind turbines turning`}
                  rate={1}
                  className="opacity-75 transition-opacity duration-500 group-hover:opacity-90"
                  testId={`industry-video-${industry.key}`}
                />
              ) : (
                <img
                  src={IMAGES[industry.key]}
                  alt={`${industry.name} — instrument calibration industry`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-60 transition-[transform,opacity] duration-700 ease-out group-hover:scale-105 group-hover:opacity-80"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-[#0A0F1A]/30 to-transparent transition-opacity duration-500 group-hover:opacity-70" />
              <span
                aria-hidden="true"
                className="absolute right-3 top-3 h-4 w-4 border-r-2 border-t-2 border-[#3DB5FF] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
              <div className="absolute bottom-0 left-0 p-5">
                <p className="hud-label photo-tile-label mb-1.5">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="photo-tile-text font-heading text-lg font-semibold tracking-tight md:text-xl">
                  {industry.name}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
